import {
    IonButton,
    IonCard,
    IonCardContent,
    IonCardTitle,
    IonCol,
    IonInput,
    IonItem,
    IonLabel,
    IonRow,
    IonText,
} from "@ionic/react";
import React, { useEffect, useState } from "react";
import { CreateHorseInput, UpdateEventEntryInput, UpdateHorseInput } from "../../../../../API";
import { Event, EventEntry, Horse } from "../../../../../models";
import { updateEventEntry } from "../../../../../utilities/eventEntry/EventEntry";
import { calculateHeightInHands } from "../../../../../utilities/horse/Height";
import { createHorse, updateHorse } from "../../../../../utilities/horse/Horse";
import SuccessBanner from "../../../../Banners/SuccessBanner";
import ErrorAlert from "../../../../Errors/ErrorAlert";
import HorseHeightInput from "../../../../Horse/HorseHeightInput";
import Spinner from "../../../../Spinners/Spinner";
import { breeds } from "../../../../../utilities/horse/Breed";
import { Input } from "reactstrap";

interface _Props {
    entry: EventEntry
    onUpdateEntry: (entry: EventEntry) => void
}

const EditEntryHorseDetailsCard: React.FC<_Props> = ({entry, onUpdateEntry}) => {

    const [isLoading, setIsLoading] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [horseName, setHorseName] = useState("");
    const [height, setHeight] = useState(0);
    const [fraction, setFraction] = useState("");
    const [yearBorn, setYearBorn] = useState("");
    const [breed, setBreed] = useState("");
    const [sex, setSex] = useState("");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const getEntryInformation = (entry: EventEntry) => {
        setHorseName(entry.horseName || "");
        setHeight(parseInt(entry?.horse?.height || "0") || 0);
    }

    useEffect(() => {
        if (entry) getEntryInformation(entry);
    }, [entry]);

    const handleHeightChange = (heightInInches: number, fraction: string) => {
        setHeight(heightInInches);
        setFraction(fraction);
    }

    const validateForm = () => {
        if (!horseName) {
            setError("Please include a name for the horse.");
            return false;
        }
        return true;
    }

    const handleCreateHorse = async () => {
        // If there is not a horse currently attached to the entry, it is probably because the entry was created via Quick Add
        // There needs to be some sort of horse attached in order to store other horse details (including Coggins info), so create a new horse. 
        // TO DO - should the user be prompted to find the horse in the DB first?
        // TO DO - should this horse be attached to the barn on the entry?
        let input: CreateHorseInput = {
            name: horseName,
            height: height ? height.toString() : "",
            fraction: fraction,
            breed: breed,
            sex: sex,
            ownerName: entry.ownerName || entry.owner?.name || "",
            ownerId: entry.ownerId || null,
            barnId: entry.barnId || null,
            barnName: entry.barnName || entry.barn?.name || ""
        };
        const createResult = await createHorse(input);
        if (createResult.isSuccess) {
            const newHorse: Horse = createResult.result;
            setSuccess("Created the horse info.");
            return newHorse;
        } else {
            setError("Could not create the horse");
            return null;
        }
    }

    const handleEditHorse = async (horseId: string) => {
        let input: UpdateHorseInput = {
            id: horseId,
            name: horseName,
            height: height ? height.toString() : "",
            fraction: fraction,
            breed: breed,
            sex: sex
        };
        const updateResult = await updateHorse(input);
        if (updateResult.isSuccess) {
            setSuccess("Updated the horse info.");
        } else {
            setError("Could not update the horse");
        }
    }

    const updateHorseOnEntry = async (horseId?: string) => {
        let updateEntryInput: UpdateEventEntryInput = {
            id: entry.id,
            horseName: horseName
        };
        if (horseId) updateEntryInput["horseId"] = horseId;
        const updateEntryResult = await updateEventEntry(updateEntryInput);
        if (updateEntryResult.isSuccess) {
            const updatedEntry: EventEntry = updateEntryResult.result;
            onUpdateEntry(updatedEntry);
            setSuccess("Updated the horse info on the entry.");
        } else {
            setError("Could not update the entry.");
        }
    }

    const handleSubmit = async () => {
        setIsLoading(true);
        setError("");
        setSuccess("");

        const isValid = validateForm();
        if (isValid) {
            // Track the horseId - if a new horse is created, horseId is needed for Update Entry
            let horseId: string | undefined = entry.horseId || undefined;

            // Next, try to update the horse object or create a new horse
            if (entry && entry.horseId) {
                await handleEditHorse(entry.horseId);
            } else {
                const newHorse = await handleCreateHorse();
                if (newHorse) horseId = newHorse.id;
            }

            // Next, try to edit the entry
            await updateHorseOnEntry(horseId);
        }

        setIsEdit(false);
        setIsLoading(false);
    }

    return (
        <>
            <IonCard color="white" className="ion-padding">
                <IonCardTitle>
                    <IonRow onClick={() => setIsCollapsed(!isCollapsed)}>
                        <IonCol>
                            <h4>Horse Details</h4>
                        </IonCol>
                        <IonCol className="ion-text-right">
                            <p>{isCollapsed ? "Expand" : "Collapse"}</p>
                        </IonCol>
                    </IonRow>
                </IonCardTitle>
                {!isCollapsed && (
                    <IonCardContent>
                        {error && <ErrorAlert width="12" error={error} />}
                        {success && <SuccessBanner width="12" success={success} />}
                        {isEdit ?
                            <>
                                <IonRow>
                                    <IonCol sizeXs="12" sizeMd="6">
                                        <IonItem color="white">
                                            <IonLabel position="stacked" className="ion-text-wrap">Horse Name</IonLabel>
                                            <IonInput 
                                                type="text"
                                                value={horseName}
                                                aria-required={true}
                                                onIonChange={e => {
                                                    setHorseName(e.detail.value!);
                                                }}
                                            />
                                        </IonItem>
                                    </IonCol>
                                    <IonCol sizeXs="12" sizeMd="6">
                                        <IonItem color="white">
                                            <IonLabel position="stacked" className="ion-text-wrap">Year Born</IonLabel>
                                            <IonInput 
                                                type="text"
                                                value={yearBorn}
                                                aria-required={true}
                                                onIonChange={e => {
                                                    setYearBorn(e.detail.value!);
                                                }}
                                            />
                                        </IonItem>
                                    </IonCol>
                                </IonRow>
                                <IonRow>
                                    <IonCol sizeXs="12" sizeMd="12">
                                        <HorseHeightInput isRequired={false} heightInInches={height} fraction={fraction} onChange={(i, f) => handleHeightChange(i, f)} />
                                    </IonCol>
                                </IonRow>
                                <IonRow>
                                    <IonCol sizeXs="12" sizeMd="6">
                                        <IonLabel position="stacked" className="ion-text-wrap">Breed</IonLabel>
                                        <Input 
                                            type="select" 
                                            name="select"
                                            value={breed}
                                            id="breedSelect"
                                            onChange={e => setBreed(e.target.value)}
                                        >
                                            <option>-</option>
                                            {breeds.map((breed, index) => (
                                                <option key={index}>{breed}</option>
                                            ))}
                                        </Input>
                                    </IonCol>
                                    <IonCol sizeXs="12" sizeMd="6">
                                        <IonLabel position="stacked" className="ion-text-wrap">Sex</IonLabel>
                                        <Input 
                                            type="select" 
                                            name="select"
                                            value={sex}
                                            id="sexSelect"
                                            onChange={e => setSex(e.target.value)}
                                        >
                                            <option>-</option>
                                            <option>Gelding</option>
                                            <option>Mare</option>
                                            <option>Stallion</option>
                                            <option>Colt</option>
                                        </Input>
                                    </IonCol>
                                </IonRow>
                            </>
                            :
                            <>
                                <IonRow>
                                    <IonCol sizeXs="12" sizeMd="6">
                                        <IonItem color="white">
                                            <IonLabel position="stacked" className="ion-text-wrap">Horse Name</IonLabel>
                                            <IonText><p>{horseName}</p></IonText>
                                        </IonItem>
                                    </IonCol>
                                    <IonCol sizeXs="12" sizeMd="6">
                                        <IonItem color="white">
                                            <IonLabel position="stacked" className="ion-text-wrap">Year Born</IonLabel>
                                            <IonText><p>{yearBorn}</p></IonText>
                                        </IonItem>
                                    </IonCol>
                                </IonRow>
                                <IonRow>
                                    <IonCol sizeXs="12" sizeMd="6">
                                        <IonItem color="white">
                                            <IonLabel position="stacked" className="ion-text-wrap">Hands</IonLabel>
                                            <IonText><p>{calculateHeightInHands(height)}</p></IonText>
                                        </IonItem>
                                    </IonCol>
                                    <IonCol sizeXs="12" sizeMd="6">
                                        <IonItem color="white">
                                            <IonLabel position="stacked" className="ion-text-wrap">Fraction</IonLabel>
                                            <IonText><p>{fraction}</p></IonText>
                                        </IonItem>
                                    </IonCol>
                                </IonRow>
                                <IonRow>
                                    <IonCol sizeXs="12" sizeMd="6">
                                        <IonItem color="white">
                                            <IonLabel position="stacked" className="ion-text-wrap">Breed</IonLabel>
                                            <IonText><p>{breed}</p></IonText>
                                        </IonItem>
                                    </IonCol>
                                    <IonCol sizeXs="12" sizeMd="6">
                                        <IonItem color="white">
                                            <IonLabel position="stacked" className="ion-text-wrap">Sex</IonLabel>
                                            <IonText><p>{sex}</p></IonText>
                                        </IonItem>
                                    </IonCol>
                                </IonRow>
                            </>
                        }
                        {isLoading ?
                            <Spinner />
                            :
                            <IonRow className="ion-justify-content-center">
                                {isEdit && (
                                    <IonCol sizeMd="4" className="ion-text-center">
                                        <IonButton
                                            className="ion-margin-top"
                                            color="primary"
                                            expand="block"
                                            onClick={() => setIsEdit(false)}
                                        >
                                            Cancel
                                        </IonButton>
                                    </IonCol>
                                )}
                                <IonCol sizeMd="4" className="ion-text-center">
                                    <IonButton
                                        className="ion-margin-top"
                                        color="tertiary"
                                        expand="block"
                                        onClick={isEdit ? handleSubmit : (() => setIsEdit(true))}
                                    >
                                        {!isEdit && "Allow "}Edit
                                    </IonButton>
                                </IonCol>
                            </IonRow>
                        }
                    </IonCardContent>
                )}
            </IonCard>
        </>
    );
};

export default EditEntryHorseDetailsCard;